import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { toast } from 'react-toastify';
import { FiArrowLeft, FiCheck, FiLoader } from 'react-icons/fi';
import api from '../axios';
import { useAuth } from '../context/AuthContext';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY || '');

const planFeatureMap = {
  monthly: [
    'Configure forex and crypto symbol lists',
    'Set max trades and max pending orders',
    'Define daily profit and loss thresholds',
    'Adjust weekday and weekend trade caps',
    'Access the full account area and automation workflow',
    'Standard support coverage',
  ],
  yearly: [
    'Configure forex and crypto symbol lists',
    'Set max trades and max pending orders',
    'Define daily profit and loss thresholds',
    'Adjust weekday and weekend trade caps',
    'Access the full account area and automation workflow',
    'Lower effective cost at $40.83/month',
  ],
};

const CheckoutForm = ({ clientSecret, paymentIntentId, planId, amount, planName }) => {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(false);
  const [cardComplete, setCardComplete] = useState(false);
  const [error, setError] = useState(null);
  const [email, setEmail] = useState('');
  const { user } = useAuth();
  const billingLabel = planId === 'yearly' ? 'Billed yearly' : 'Billed monthly';
  const billingUnit = planId === 'yearly' ? 'year' : 'month';
  const includedFeatures = planFeatureMap[planId] || planFeatureMap.monthly;

  useEffect(() => {
    setEmail(user?.email || '');
  }, [user]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements || !clientSecret) {
      setError('Payment service not ready');
      return;
    }

    if (!cardComplete) {
      setError('Please complete your card information');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Confirm the payment
      const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(
        clientSecret,
        {
          payment_method: {
            card: elements.getElement(CardElement),
            billing_details: {
              email: email,
            },
          },
        }
      );

      if (confirmError) {
        setError(confirmError.message);
        toast.error(`Payment failed: ${confirmError.message}`);
      } else if (paymentIntent.status === 'succeeded') {
        // Confirm subscription with backend
        const response = await api.post('/api/subscription/confirm', {
          plan_id: planId,
          payment_intent_id: paymentIntentId,
        });

        if (response.data?.success) {
          toast.success('✅ Payment successful! Welcome to ' + planName + '!');
          setTimeout(() => {
            navigate('/account');
          }, 2000);
        } else {
          throw new Error(response.data?.detail || 'Failed to confirm subscription');
        }
      } else if (paymentIntent.status === 'requires_action') {
        // 3D Secure or other authentication required
        toast.info('Additional verification required. Please follow the prompts.');
      }
    } catch (err) {
      console.error('Payment error:', err);
      setError(err.message || 'Payment processing failed');
      toast.error(err.message || 'Payment processing failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid md:grid-cols-2 gap-12">
      {/* Order Summary */}
      <div className="order-2 md:order-1">
        <div className="bg-gray-800 rounded-lg p-8 border border-gray-700 sticky top-8">
          <h3 className="text-lg font-bold mb-6">Order Summary</h3>
          
          <div className="space-y-4 pb-6 border-b border-gray-700">
            <div className="flex justify-between items-center">
              <span className="text-gray-300">{planName} Plan</span>
              <span className="font-bold">${amount / 100}</span>
            </div>
            <div className="flex justify-between items-center text-sm">
              <span className="text-gray-400">{billingLabel}</span>
              <span className="text-gray-400">Recurring</span>
            </div>
            <div className="flex justify-between items-center text-sm">
              <span className="text-gray-400">7-day trial included</span>
              <span className="text-green-400">Free</span>
            </div>
          </div>

          <div className="pt-6 mb-6">
            <div className="flex justify-between items-center">
              <span className="text-gray-300">Amount Due Today</span>
              <span className="text-2xl font-bold">${(amount / 100).toFixed(2)}</span>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              First charge after 7-day trial. Cancel before renewal if needed.
            </p>
          </div>

          {/* Included Features */}
          <div className="mt-8">
            <h4 className="font-bold mb-4 text-sm">You'll get access to:</h4>
            <ul className="space-y-3 text-sm">
              {includedFeatures.map((feature, idx) => (
                <li key={idx} className="flex items-center gap-2 text-gray-300">
                  <FiCheck className="w-4 h-4 text-green-500" />
                  {feature}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Payment Form */}
      <div className="order-1 md:order-2">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Email */}
          <div>
            <label className="block text-sm font-medium mb-2">Email Address</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              required
              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:border-green-500 focus:outline-none transition"
            />
          </div>

          {/* Card Element */}
          <div>
            <label className="block text-sm font-medium mb-2">Card Information</label>
            <div className="bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 focus-within:border-green-500 transition">
              <CardElement
                onComplete={() => setCardComplete(true)}
                onChange={(e) => {
                  setCardComplete(e.complete);
                  if (e.error) {
                    setError(e.error.message);
                  } else {
                    setError(null);
                  }
                }}
                options={{
                  style: {
                    base: {
                      fontSize: '16px',
                      color: '#ffffff',
                      backgroundColor: '#374151',
                      '::placeholder': {
                        color: '#9ca3af',
                      },
                    },
                    invalid: {
                      color: '#ef4444',
                    },
                  },
                }}
              />
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-900/20 border border-red-700 rounded-lg p-4">
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}

          {/* Billing Info */}
          <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
            <p className="text-xs text-gray-400">
              <strong>Billing:</strong> Starting at ${(amount / 100).toFixed(2)}/{billingUnit} after 7-day trial. 
              Cancel anytime. See our <Link to="/terms" className="text-green-400 hover:underline">Terms of Service</Link>.
            </p>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading || !stripe || !elements}
            className="w-full bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 disabled:from-gray-600 disabled:to-gray-600 text-black font-bold py-4 rounded-lg transition-all duration-300 flex items-center justify-center gap-2 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <FiLoader className="w-5 h-5 animate-spin" />
                Processing Payment...
              </>
            ) : (
              <>
                <FiCheck className="w-5 h-5" />
                Complete Purchase (${(amount / 100).toFixed(2)})
              </>
            )}
          </button>

          {/* Secure Payment Badge */}
          <div className="flex items-center justify-center gap-2 text-xs text-gray-500">
            <span>🔒</span>
            <span>Secure payment powered by Stripe</span>
          </div>
        </form>
      </div>
    </div>
  );
};

export default function CheckoutPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state;

  useEffect(() => {
    // Redirect if no checkout state (user navigating directly)
    if (!state?.clientSecret) {
      navigate('/pricing');
    }
  }, [state, navigate]);

  if (!state?.clientSecret) {
    return null;
  }

  return (
    <div className="min-h-screen bg-black text-white py-12">
      <div className="max-w-6xl mx-auto px-4">
        {/* Header */}
        <div className="mb-12">
          <button
            onClick={() => navigate('/pricing')}
            className="flex items-center gap-2 text-gray-400 hover:text-white mb-8 transition"
          >
            <FiArrowLeft className="w-4 h-4" />
            Back to Pricing
          </button>

          <div>
            <h1 className="text-4xl font-bold mb-2">Complete Your Purchase</h1>
            <p className="text-gray-400">
              Upgrade to {state.planName} and start trading with AI power
            </p>
          </div>
        </div>

        {/* Checkout Container */}
        <div className="bg-gray-900 rounded-xl border border-gray-800 p-8">
          <Elements stripe={stripePromise}>
            <CheckoutForm
              clientSecret={state.clientSecret}
              paymentIntentId={state.paymentIntentId}
              planId={state.planId}
              amount={state.amount}
              planName={state.planName}
            />
          </Elements>
        </div>

        {/* Trust Badges */}
        <div className="mt-12 grid md:grid-cols-3 gap-8">
          <div className="text-center">
            <div className="text-2xl mb-2">🔒</div>
            <h4 className="font-bold mb-1">PCI Compliant</h4>
            <p className="text-sm text-gray-400">Your card data is 100% secure</p>
          </div>
          <div className="text-center">
            <div className="text-2xl mb-2">💳</div>
            <h4 className="font-bold mb-1">Stripe Powered</h4>
            <p className="text-sm text-gray-400">Trusted by millions worldwide</p>
          </div>
          <div className="text-center">
            <div className="text-2xl mb-2">⏮️</div>
            <h4 className="font-bold mb-1">Easy Cancellation</h4>
            <p className="text-sm text-gray-400">Cancel anytime, no questions asked</p>
          </div>
        </div>
      </div>
    </div>
  );
}
